// @flow
import type { Node } from 'react';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import styled, { withTheme } from 'styled-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _ from 'lodash';
import { toast } from 'react-toastify';
import Menu from 'material-ui/Menu/Menu';
import MaterialPopover from 'material-ui/Popover/Popover';
import AlertIcon from 'material-ui/svg-icons/social/notifications';
import IconButton from 'material-ui/IconButton/IconButton';
import DeleteAllIcon from 'material-ui/svg-icons/action/delete';

import FlatButton from 'material-ui/FlatButton';
import Badge from 'material-ui/Badge';
import { Tooltip } from '@material-ui/core';
import Notification from '../domain/Notification';
import { clearNotifications, removeNotification, updateNotification } from '../actions/uiActions';
import { listNotifications } from '../selectors/notificationSelectors';
import NotificationContent from './NotificationContent';
import NotificationToast from './NotificationToast';
import NotificationCard from './NotificationCard';
import Radio from '../../../domain/radio';
import DevFlag from '../../../components/DevFlag';

const Popover = styled(MaterialPopover)`
  &:after {
    bottom: 100%;
    right: 14px;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #ffffff;
    border-width: 10px;
    margin-left: -10px;
  }
`;

const NotificationsTitle = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const NotificationsSection = styled.div`
  border-top: transparent 2px solid;
  border-bottom: rgba(0,0,0,0.1) 2px solid;
`;

const NotificationsHeader = styled(NotificationsSection)`
  padding: ${({ theme }) => theme.metrics.margin}px;
`;

const NotificationsSubHeader = styled(NotificationsSection)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.metrics.smallMargin}px;
`;

const NotificationsWrapper = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const NoNotificationWrapper = styled.div`
  padding: ${({ theme }) => theme.metrics.margin}px;
`;

const SIcon = styled.div`
  height: ${props => props.theme.V3.metrics.icon.frameSize}px;
  width: ${props => props.theme.V3.metrics.icon.frameSize}px;
  padding: ${props => props.theme.V3.metrics.icon.framePadding}px;
  cursor: pointer;
`;

export type NotificationRenderer = {
  [type: string]: () => Node,
}

type Props = {
  notifications: Array<Notification>,
  actions: {
    removeNotification: (reference: string) => any,
    updateNotification: (notification: Notification) => any,
    clearNotifications: () => any,
  },
  switchRadio?: (radio: Radio) => any,
  t: any,
  history: any,
  location: any,
  theme: any,

  notificationRenderers: NotificationRenderer,
};

type State = {
  open: boolean,
  anchorEl: ?any,
};

class NotificationsPanel extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
      anchorEl: undefined,
    };
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !_.isEqual(nextProps.notifications, this.props.notifications)
      || nextState.open !== this.state.open;
  }

  componentWillReceiveProps(nextProps: Props) {
    const toToast = nextProps.notifications.filter(n => n.toast);
    if (toToast.length === 0) {
      return;
    }

    const notif = toToast[toToast.length - 1];
    toast(<NotificationToast
      notification={notif}
      onClick={n => this.onNotificationClick(n)}
      renderNotification={n => this.renderNotification(n)}
    />);

    nextProps.actions.updateNotification(notif.sendToast());
  }

  renderNotification(notification: Notification) {
    const { notificationRenderers, t } = this.props;
    const ToRender = notificationRenderers[notification.getType()] || NotificationContent;
    return <ToRender notification={notification} t={t} />;
  }

  openMenu(anchorEl: any) {
    this.setState({ open: true, anchorEl });
  }

  closeMenu() {
    this.setState({ open: false });
  }

  onReadAll(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.notifications.forEach(n => this.props.actions.updateNotification(n.markRead()));
  }

  onDeleteAll(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.actions.clearNotifications();
  }

  async onNotificationClick(notification: Notification) {
    this.props.actions.updateNotification(notification.markRead());
    if (!notification.getUrl()) {
      return;
    }

    if (this.props.switchRadio && notification.radio) {
      await this.props.switchRadio(notification.radio);
    }

    this.props.history.push(notification.getUrl());
  }

  render() {
    const {
      notifications, t, theme, actions,
    } = this.props;
    const unreadNotifications = notifications.filter(n => !n.isRead());

    const visibility = unreadNotifications.length > 0 ? 'visible' : 'hidden';
    const iconButton = (
      <Badge
        badgeContent={unreadNotifications.length}
        badgeStyle={{
          color: 'inherit', backgroundColor: theme.colors.badge, width: 20, height: 20, visibility,
        }}
        style={{ padding: 0 }}
        onClick={e => this.openMenu(e.currentTarget)}
      >
        <DevFlag
          flag={'UI_V2'}
          fallback={
            <IconButton
              style={{ color: 'inherit' }}
              iconStyle={{ color: 'inherit' }}
              tooltip={t('notifications')}
            >
              <AlertIcon />
            </IconButton>
          }
        >
          <Tooltip title={t('notifications')}>
            <SIcon>
              <AlertIcon color={theme.V3.colors.text.tertiary} />
            </SIcon>
          </Tooltip>
        </DevFlag>
      </Badge>
    );

    return (
      <div>
        {iconButton}
        <Popover
          anchorEl={this.state.anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          targetOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={this.state.open}
          style={{ zIndex: 2100 }}
          onRequestClose={() => this.closeMenu()}
          zDepth={2}
        >
          <Menu style={{ width: 360 }} listStyle={{ paddingTop: 0, paddingBottom: 0 }}>
            <NotificationsHeader>
              <NotificationsTitle>{t('notifications')}</NotificationsTitle>
            </NotificationsHeader>
            <NotificationsSubHeader>
              <FlatButton
                label={t('readAll')}
                onClick={e => this.onReadAll(e)}
                labelStyle={{ fontSize: 14, fontWeight: 400, textTransform: 'none' }}
              />
              <FlatButton
                label={t('deleteAll')}
                labelPosition="before"
                onClick={e => this.onDeleteAll(e)}
                icon={<DeleteAllIcon />}
                labelStyle={{ fontSize: 14, fontWeight: 400, textTransform: 'none' }}
              />
            </NotificationsSubHeader>
            <NotificationsWrapper>
              {notifications.map(notification =>
                (<NotificationCard
                  key={notification.reference}
                  notification={notification}
                  t={t}

                  onClick={n => this.onNotificationClick(n)}
                  onRead={n => actions.updateNotification(n.toggleRead())}
                  onRemove={n => actions.removeNotification(n)}
                  renderNotification={n => this.renderNotification(n)}
                />))}
              {!notifications.length &&
                <NoNotificationWrapper>
                  {t('noNotifications')}
                </NoNotificationWrapper>
              }
            </NotificationsWrapper>
          </Menu>
        </Popover>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    notifications: listNotifications(state),
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({
      removeNotification,
      updateNotification,
      clearNotifications,
    }, dispatch),
  };
}

export default translate('commonNotifs')(connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(NotificationsPanel))));



// WEBPACK FOOTER //
// ./src/common/modules/ui/components/NotificationsPanel.js